import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/accordion';
import { Button, Spinner } from 'components';
import { AudioFileSelector } from './AudioFileSelector';
import { useTranslation } from 'react-i18next';
import { type AudioUploadStartOptions, UploadStep, useAudioUpload } from 'hooks/useAudioUpload';
import { getApiErrorMessage, isRecordingQualityError } from 'utils/apiError';

const UPLOAD_STEP_TRANSLATION_KEYS: Partial<Record<UploadStep, string>> = {
  [UploadStep.PREPARING]: 'recording.uploadStatus.preparing',
  [UploadStep.TRANSCODING]: 'recording.uploadStatus.transcoding',
  [UploadStep.STREAMING]: 'recording.uploadStatus.streaming',
  [UploadStep.WAITING_FOR_RESULT]: 'recording.uploadStatus.waitingForResult',
};

export const AudioUploader = ({ startOptions }: { startOptions: AudioUploadStartOptions }) => {
  const { t } = useTranslation();
  const {
    handleAudioFileSelect,
    handleUploadFile,
    uploadedAudioFile,
    isUploading,
    uploadStep,
    uploadError,
    clearSelectedFile,
    cancelUpload,
  } = useAudioUpload();

  if (uploadError) {
    const errorMessage = isRecordingQualityError(uploadError)
      ? t('errors.recordingQualityError')
      : getApiErrorMessage(uploadError, t, 'errors.uploadingRecording');

    return (
      <div className="text-center space-y-4">
        <h1>{errorMessage}</h1>
        <Button onClick={clearSelectedFile}>{t('general.dismiss')}</Button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="audio-upload">
          <AccordionTrigger>{t('recording.uploadAudioFile')}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{t('recording.uploadAudioDescription')}</p>
              <AudioFileSelector
                onFileSelect={handleAudioFileSelect}
                onRemove={cancelUpload}
                className="w-full"
              />
              {uploadedAudioFile && (
                <div className="flex justify-center">
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Spinner size="sm" />
                      {UPLOAD_STEP_TRANSLATION_KEYS[uploadStep] && (
                        <span className="text-xs text-gray-500">
                          {t(UPLOAD_STEP_TRANSLATION_KEYS[uploadStep])}
                        </span>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={() => void handleUploadFile(startOptions)}
                      className="w-full max-w-xs"
                    >
                      {t('recording.processAudioFile')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default AudioUploader;
